import { type ScaleLinear } from "d3";
import { debounce } from "lodash-es";
import { equals } from "ramda";
import { PureComponent } from "react";
import { connect } from "react-redux";
import { notificationDismiss } from "widgets";

import { getExtendedRange, rangeContainsRange, rangeEquals } from "../../../app-utils/range.js";
import { bedShapePileupParams } from "../../../app-utils/track-shapes/shapes-settings.js";
import { type IPileup, PileupFactory } from "../../../app-utils/track-shapes/track-pileup-factory.js";
import { getPileupHeight } from "../../../app-utils/track-shapes/track-utils.js";
import {
  type Contig,
  type Feature,
  type ProjectFile,
  type Range,
  type Track,
  type TrackBedOrderedBedFile,
  type TrackFeatures,
} from "../../../graphql.js";
import { notify } from "../../../modules/notifications/notify.js";
import {
  fetchTrackBedFeatures,
  getTrackId,
  type IGenomeViewerStateLayout,
  type ITrackData,
  loadBedFile,
  validateBedFileAgainstRefGenomes,
} from "../../../redux/modules/genome-browser.js";
import { type IAppState } from "../../../redux/reducer.js";
import { DEFAULT_TRACK_HEIGHT } from "../../../settings/settings.js";
import { type SetTooltipContent } from "../GenomeViewerTracksList.js";
import { BedTrack } from "./BedTrack.js";

export interface IBedTrackData extends TrackFeatures, ITrackData {
  showAllFeatures: boolean;
}

export const INITIAL_BED_TRACK_DATA: Partial<IBedTrackData> = {
  fileId: null,
  features: [],
  showAllFeatures: false,
};

interface IBedTrackLoaderOwnProps {
  track: Track; // track spec
  bedFileData: TrackBedOrderedBedFile;
  scale: ScaleLinear<number, number>;
  setTooltipContent: SetTooltipContent;
}

interface IBedTrackLoaderAppStateProps {
  trackData: IBedTrackData;
  range: Range;
  contig: Contig;
  layout: IGenomeViewerStateLayout;
  bedFile: ProjectFile;
}

interface IBedTrackLoaderDispatchProps {
  loadBedFile: (bedFileId: number) => Promise<ProjectFile>;
  showWarning: (warningMessage: string, bedFileId: string | number) => void;
  fetchTrackBedFeatures: (track: Track, contig: Contig, range: Range, bedFileId: number) => Promise<TrackFeatures>;
  validateBedFileAgainstRefGenomes: (bedFile: ProjectFile) => Promise<string>;
}

interface IBedTrackLoaderProps
  extends IBedTrackLoaderAppStateProps,
    IBedTrackLoaderDispatchProps,
    IBedTrackLoaderOwnProps {}

function mapStateToProps(state: IAppState, ownProps: IBedTrackLoaderOwnProps): IBedTrackLoaderAppStateProps {
  const trackId: string = getTrackId({ ...ownProps.track, fileId: ownProps.bedFileData.bedFileId });

  return {
    range: state.genomeBrowser.range,
    contig: state.genomeBrowser.contig,
    trackData: state.genomeBrowser.trackDataMap[trackId] as IBedTrackData,
    layout: state.genomeBrowser.layout,
    bedFile: state.genomeBrowser.bedDatafiles[ownProps.bedFileData.bedFileId],
  };
}

function mapDispatchToProps(dispatch): IBedTrackLoaderDispatchProps {
  return {
    loadBedFile: (bedFileId: number) => dispatch(loadBedFile(bedFileId)),
    fetchTrackBedFeatures: (track: Track, contig: Contig, range: Range, bedFileId: number) =>
      dispatch(fetchTrackBedFeatures(track, contig, range, bedFileId)),
    validateBedFileAgainstRefGenomes: (bedFile: ProjectFile) => dispatch(validateBedFileAgainstRefGenomes(bedFile)),
    showWarning: (warningMessage: string, bedFileId) =>
      notify.warning(
        {
          title: "Mismatch",
          children: warningMessage,
        },
        { autoClose: false, toastId: bedFileId },
      ),
  };
}

interface IBedTrackLoaderState {
  pileup: IPileup;
  trackMessage: string;
}

class BedTrackLoader extends PureComponent<IBedTrackLoaderProps, IBedTrackLoaderState> {
  state = {
    pileup: null,
    trackMessage: null,
  };
  private pileupFactory: PileupFactory;

  loadData = () => {
    const bedFile = this.props.bedFile;
    if (bedFile && !bedFile.hidden && !bedFile.deleted) {
      const extendedRange = getExtendedRange(this.props.contig, this.props.range);
      void this.props.fetchTrackBedFeatures(
        this.props.track,
        this.props.contig,
        extendedRange,
        this.props.bedFileData.bedFileId,
      );
    }
  };

  debouncedLoadData = debounce(this.loadData, 300, { maxWait: 1000 });

  loadAndValidateBedFile = () => {
    void this.props.loadBedFile(this.props.bedFileData.bedFileId).then((bedFile: ProjectFile) => {
      if (bedFile?.hidden) {
        this.setState({ trackMessage: "This BED file is not accessible to you." });
      } else if (bedFile?.deleted) {
        this.setState({ trackMessage: "This BED file has been deleted." });
      } else {
        this.debouncedLoadData();
        void this.props.validateBedFileAgainstRefGenomes(bedFile).then((warning: string) => {
          if (warning) {
            this.props.showWarning(`${bedFile.fileName}: ${warning}`, bedFile.id);
          }
        });
      }
    });
  };

  componentDidMount() {
    this.pileupFactory = new PileupFactory(this.props.scale);

    this.loadAndValidateBedFile();
  }

  componentDidUpdate(prevProps: Readonly<IBedTrackLoaderProps>, _prevState: Readonly<IBedTrackLoaderState>) {
    if (this.props.scale !== prevProps.scale) {
      this.pileupFactory = new PileupFactory(this.props.scale);
    }

    const rangeDiffers = !rangeEquals(prevProps.range, this.props.range);
    const contigDiffers: boolean = prevProps.contig?.id !== this.props.contig?.id;
    const boxModelDiffers = !equals(this.props.layout, prevProps.layout);

    // logic about data fetching
    if (rangeDiffers || contigDiffers) {
      const missingData: boolean =
        !this.props.trackData?.range ||
        !rangeContainsRange(this.props.trackData?.range, this.props.range) ||
        this.props.trackData?.overflow;

      if (contigDiffers) {
        // directly fetch data again
        this.loadData();
      } else if (missingData) {
        // debounced data fetching until range is stabilized
        this.debouncedLoadData();
      }
    }

    const trackDataDiffers: boolean = this.props.trackData !== prevProps.trackData;

    // update the layout if the data do not overflow
    if (!this.props.trackData?.overflow) {
      if (trackDataDiffers || rangeDiffers) {
        this.updateLayout(this.props.trackData?.features);
      } else if (boxModelDiffers) {
        this.debouncedUpdateLayout(this.props.trackData?.features);
      }
    }
  }
  componentWillUnmount() {
    notificationDismiss(this.props.bedFileData.bedFileId);
  }

  updateLayout = (bedFeatures: Feature[]) => {
    if (bedFeatures == undefined) {
      return;
    }

    const pileup: IPileup = this.pileupFactory.buildFeaturePileup(
      bedFeatures,
      this.props.range,
      bedShapePileupParams,
      this.props.trackData?.showAllFeatures,
      0,
    );

    this.setState({ pileup: pileup });
  };

  debouncedUpdateLayout = debounce(this.updateLayout, 300, { maxWait: 1000 });

  getTrackHeight = () => {
    return this.props.trackData?.overflow ? DEFAULT_TRACK_HEIGHT : getPileupHeight(this.state.pileup);
  };

  render() {
    return (
      <BedTrack
        track={this.props.track}
        bedFileData={this.props.bedFileData}
        featureColor={this.props.bedFileData.color}
        trackHeight={this.getTrackHeight()}
        pileup={this.state.pileup}
        overflow={this.props.trackData?.overflow}
        empty={false}
        showAllFeatures={this.props.trackData?.showAllFeatures}
        setTooltipContent={this.props.setTooltipContent}
        trackMessage={this.state.trackMessage}
      />
    );
  }
}

const SmartBedTrackLoader = connect<
  IBedTrackLoaderAppStateProps,
  IBedTrackLoaderDispatchProps,
  IBedTrackLoaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(BedTrackLoader);

export { SmartBedTrackLoader as BedTrackLoader };
